export const ADMIN_SESSION_KEY = "trystomatic.admin.authenticated";
export const ADMIN_RANKINGS_KEY = "trystomatic.admin.rankings";
export const ADMIN_BLEND_KEY = "trystomatic.admin.blendWeight";

export function playerBlendKey(player) {
  return String(player?.sleeperId || player?.id || player?.name || "");
}

function fantasyCalcValue(player) {
  return Number(player?.fcValue ?? player?.value ?? player?.combinedValue ?? 0) || 0;
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function getAdminRankings() {
  return readJson(ADMIN_RANKINGS_KEY, null);
}

export function saveAdminRankings(order) {
  const payload = { order, savedAt: new Date().toISOString() };
  localStorage.setItem(ADMIN_RANKINGS_KEY, JSON.stringify(payload));
  return payload;
}

export function getBlendWeight() {
  const stored = Number(localStorage.getItem(ADMIN_BLEND_KEY));
  return Number.isFinite(stored) ? Math.min(1, Math.max(0, stored)) : 0.5;
}

export function saveBlendWeight(weight) {
  const normalized = Math.min(1, Math.max(0, Number(weight) || 0));
  localStorage.setItem(ADMIN_BLEND_KEY, String(normalized));
  return normalized;
}

function buildEffectiveAdminOrder(players, savedOrder = []) {
  const known = new Set(players.map(playerBlendKey));
  const used = new Set();
  const order = [];

  for (const key of savedOrder) {
    if (known.has(key) && !used.has(key)) {
      order.push(key);
      used.add(key);
    }
  }

  const remaining = [...players]
    .filter((player) => !used.has(playerBlendKey(player)))
    .sort((a, b) => fantasyCalcValue(b) - fantasyCalcValue(a));

  for (const player of remaining) order.push(playerBlendKey(player));
  return order;
}

export function applyAdminBlendToPlayers(players, options = {}) {
  const adminRankings = getAdminRankings();
  const savedOrder = options.order || adminRankings?.order || [];
  const hasSavedRankings = savedOrder.length > 0;
  const blendWeight = hasSavedRankings ? (options.blendWeight ?? getBlendWeight()) : 0;
  const basePlayers = [...players];
  const baselineValues = [...basePlayers]
    .sort((a, b) => fantasyCalcValue(b) - fantasyCalcValue(a))
    .map(fantasyCalcValue);
  const effectiveOrder = buildEffectiveAdminOrder(basePlayers, savedOrder);
  const adminValueByKey = new Map();

  effectiveOrder.forEach((key, index) => {
    adminValueByKey.set(key, baselineValues[index] ?? baselineValues[baselineValues.length - 1] ?? 0);
  });

  const blended = basePlayers.map((player) => {
    const key = playerBlendKey(player);
    const fcValue = fantasyCalcValue(player);
    const adminRankValue = adminValueByKey.get(key) ?? fcValue;
    const combinedValue = Math.round((adminRankValue * blendWeight) + (fcValue * (1 - blendWeight)));
    return {
      ...player,
      fcValue,
      adminRankValue: hasSavedRankings ? adminRankValue : null,
      blendWeight,
      combinedValue,
      source: hasSavedRankings ? "admin_blend" : "fantasycalc",
    };
  });

  blended.sort((a, b) => (b.combinedValue || 0) - (a.combinedValue || 0));
  return blended.map((player, index) => ({ ...player, combinedRank: index + 1 }));
}

export function applyAdminBlendToLeague(leagueDetail, blendedPlayers) {
  if (!leagueDetail) return leagueDetail;

  const byKey = new Map();
  const byName = new Map();
  for (const player of blendedPlayers) {
    byKey.set(playerBlendKey(player), player);
    if (player.name) byName.set(player.name, player);
  }

  const rosters = (leagueDetail.rosters || []).map((roster) => {
    const players = (roster.players || []).map((asset) => {
      const match = byKey.get(playerBlendKey(asset)) || byName.get(asset.name);
      return match
        ? { ...asset, combinedValue: match.combinedValue, combinedRank: match.combinedRank, fcValue: match.fcValue }
        : asset;
    });
    const total_value = players.reduce((sum, asset) => sum + (Number(asset.combinedValue) || 0), 0);
    return { ...roster, players, total_value };
  }).sort((a, b) => (b.total_value || 0) - (a.total_value || 0));

  return { ...leagueDetail, rosters };
}
